import { useState } from 'react';
import { Button, Stack } from '@mui/material';
import {
  ToggleButton,
  ToggleButtonGroup,
  ToggleContent,
} from '@/components/shared/toggle-button-group';
import {
  EligibleResourceType,
  type Policy,
} from '@/data/subsidy/policies/interfaces';
import { RequestLimitDialog } from '@/packages/subsidy/pages/refund-request/components/request-limit-dialog';
import { StepTitle } from '@/packages/subsidy/pages/refund-request/steps/resource-description/components/step-title';
import { useRefundRequestNavigation } from '@/packages/subsidy/pages/refund-request/steps/resource-description/navigation/use-navigation';
import { RefundRequestWizardStepIds } from '@/packages/subsidy/pages/refund-request/steps/resource-description/steps/step-ids';

const stepId = RefundRequestWizardStepIds.SelectPolicy;

export const SelectPolicy = () => {
  const { handleNext, handleBack, canGoBack, values } =
    useRefundRequestNavigation({
      stepId,
    });
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [limitModalPolicy, setLimitModalPolicy] = useState<Policy | null>(null);

  const policies = values?.policies;
  const selectedPolicy = values?.selectedPolicy;

  const handleCloseLimitModal = () => {
    setShowLimitModal(false);
    setLimitModalPolicy(null);
  };

  const handleNextPolicy = (policyToUse?: Policy) => {
    const policy = policyToUse || selectedPolicy;
    const shouldShowLimitModal =
      policy?.request_limit_info &&
      !policy?.request_limit_info?.can_make_request;

    if (shouldShowLimitModal) {
      setLimitModalPolicy(policy ?? null);
      setShowLimitModal(true);
      return;
    }

    const isCatalogPolicy =
      policy?.configuration?.eligible_resource_type ===
      EligibleResourceType.Catalog;

    handleNext({
      selectedPolicy: policy,
      ...(isCatalogPolicy && {
        [RefundRequestWizardStepIds.ItemSource]: EligibleResourceType.Catalog,
      }),
    });
  };

  const handleSelection = (policy: Policy | null) => {
    if (policy) handleNextPolicy(policy);
  };

  return (
    <Stack spacing={2}>
      <StepTitle id="select-policy-label">Selecione uma política:</StepTitle>
      <ToggleButtonGroup
        aria-labelledby="select-policy-label"
        onChange={handleSelection}
        value={selectedPolicy}
      >
        {policies?.map((policy) => (
          <ToggleButton value={policy} key={policy.uuid}>
            <ToggleContent
              title={policy.name}
              description={policy.description}
            />
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      <Stack direction="row">
        {canGoBack && <Button onClick={handleBack}>Voltar</Button>}
      </Stack>
      {showLimitModal && (
        <RequestLimitDialog
          onClose={handleCloseLimitModal}
          maxRefundsPerPeriod={
            limitModalPolicy?.request_limit_info?.max_refunds_per_period
          }
          renewalPeriodInMonths={
            limitModalPolicy?.request_limit_info?.renewal_period_in_months
          }
        />
      )}
    </Stack>
  );
};
